import { useDispatch, useSelector } from "react-redux";
import DesignArea from "../tshirt-designer/components/DesignArea";
import Header from "../tshirt-designer/components/Header";
import { Toaster } from "../tshirt-designer/components/ui/toaster";
import { Canvas } from "@react-three/fiber";
import { Environment, Loader, OrbitControls } from "@react-three/drei";
import { setSelectedView } from "../tshirt-designer/features/tshirtSlice";
import { useCanvas } from "../tshirt-designer/hooks/useCanvas";
import { TshirtModel } from "../tshirt-designer/components/TShirtModel";
import { useCanvasTextureSync } from "../tshirt-designer/hooks/useCanvasTextureSync";
import { Suspense } from "react";
import { ToolsSidebar } from "../tshirt-designer/components/ToolsSidebar";
import "./tshirt-designer.css";

function TshirtDesignerApp() {
  const tshirtColor = useSelector((state) => state.tshirt.tshirtColor);
  const selectedView = useSelector((state) => state.tshirt.selectedView);
  const dispatch = useDispatch();
  const { frontCanvas, backCanvas } = useCanvas();

  const { designTextureFront, designTextureBack, manualTriggerSync } =
    useCanvasTextureSync({
      frontCanvas,
      backCanvas,
      selectedView,
    });

  const manualSync = () => {
    manualTriggerSync(selectedView);
  };

  const handleViewChange = (view) => {
    if (view !== selectedView) {
      dispatch(setSelectedView(view));
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex">
        <ToolsSidebar manualSync={manualSync} />
        <div className="flex-1">
          <div className="flex justify-center py-3 mb-2 bg-white">
            <Header />
          </div>
          <main>
            <div className="flex flex-col lg:flex-row gap-28 md:gap-10 items-center justify-around">
              <div className="h-[400px] md:h-[560px] w-[250px] md:min-w-[500px] relative mx-6 md:mx-0">
                <Canvas>
                  <OrbitControls
                    maxPolarAngle={Math.PI / 2}
                    minPolarAngle={Math.PI / 3}
                  />
                  <Suspense fallback={null}>
                    <TshirtModel
                      tshirtColor={tshirtColor}
                      onViewChange={handleViewChange}
                      designTexture={designTextureFront}
                      designTextureBack={designTextureBack}
                    />
                    <Environment preset="sunset" />
                  </Suspense>
                </Canvas>
                <Loader
                  containerStyles={{
                    position: "absolute",
                    top: 0,
                    left: 10,
                    width: "100%",
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.8)",
                    pointerEvents: "none",
                  }}
                  dataStyles={{
                    color: "#000000",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                  barStyles={{
                    backgroundColor: "#2563eb",
                    height: "2px",
                  }}
                />
                <div className="text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
                  <span>💡</span>
                  <p className="font-bold text-lg">
                    Click on the 3D t-shirt to switch between design areas
                  </p>
                </div>
              </div>

              <div className="w-[250px] md:min-w-[500px]">
                <DesignArea />
              </div>
            </div>
          </main>
        </div>
      </div>
      <Toaster />

      {/* Infinite scrolling banner */}
      <div className="fixed bottom-0 left-0 w-full bg-blue-600 py-2 overflow-hidden z-50">
        <div className="relative flex whitespace-nowrap">
          <div className="animate-marquee flex">
            <span className="text-white mx-4">🚧 Site under development</span>
            <span className="text-white mx-4">•</span>
            <span className="text-white mx-4">
              Feel free to try out the design tools
            </span>
            <span className="text-white mx-4">•</span>
            <span className="text-white mx-4">More features coming soon!</span>
            <span className="text-white mx-4">•</span>
            <span className="text-white mx-4">Thanks for visiting</span>
            <span className="text-white mx-4">•</span>
          </div>
          <div className="animate-marquee2 flex absolute top-0 left-0">
            <span className="text-white mx-4">🚧 Site under development</span>
            <span className="text-white mx-4">•</span>
            <span className="text-white mx-4">
              Feel free to try out the design tools
            </span>
            <span className="text-white mx-4">•</span>
            <span className="text-white mx-4">More features coming soon!</span>
            <span className="text-white mx-4">•</span>
            <span className="text-white mx-4">Thanks for visiting</span>
            <span className="text-white mx-4">•</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TshirtDesignerApp;
